import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import {
    SerialService,
    SerialDeviceInfo,
    SerialConnectionConfig,
    SZ_CODES as g_sz_codes,
    szPacketSize as g_sz_packet_size,
    IMPORT_ERRORS
} from './serial.service';
import { YmodemUploadService, UploadStatus } from './ymodem-upload.service';
import { FileMetadata, FileRecord } from './ymodem-storage.service';

export type ConnectState = {
    isConnected: boolean;
    isConnecting: boolean;
    isSupported: boolean;
    connectionError: string | null;
    currentDevice: SerialDeviceInfo | null;
    receivedData: string[];
    isTransferring: boolean;
    transferProgress: number;
    transferSpeedBps: number;
    currentFileName: string;
    filesReceived: number;
    uploadStatus: UploadStatus;
    fileList: FileRecord[];
};

type YmodemError = Error & {
    fatal?: boolean;
    details?: string;
};

@Injectable({
    providedIn: 'root'
})
export class ConnectLogicService implements OnDestroy {
    private subscriptions = new Subscription();
    private autoDisconnectRequested = false;
    private lastTransferTimestamp: number | null = null;
    private _transferSpeedStartTime: number | null = null;
    private _transferSpeedBytesReceived = 0;
    private _lastProgressUpdateMs = 0;

    // State management
    private stateSubject = new BehaviorSubject<ConnectState>({
        isConnected: false,
        isConnecting: false,
        isSupported: false,
        connectionError: null,
        currentDevice: null,
        receivedData: [],
        isTransferring: false,
        transferProgress: 0,
        transferSpeedBps: 0,
        currentFileName: '',
        filesReceived: 0,
        uploadStatus: {
            pending: 0,
            uploading: 0,
            completed: 0,
            failed: 0,
            totalFiles: 0
        },
        fileList: []
    });

    public state$ = this.stateSubject.asObservable();

    public constructor(
        private serialService: SerialService,
        private uploadService: YmodemUploadService
    ) {
        this.initialize();
    }

    private initialize(): void {
        const currentState = this.stateSubject.value;
        currentState.isSupported = this.serialService.isSupported();
        this.stateSubject.next(currentState);

        // Subscribe to connection status
        this.subscriptions.add(
            this.serialService.connectionStatus$.subscribe((status) => {
                const state = this.stateSubject.value;
                state.isConnected = status;
                state.isConnecting = false;
                if (status) {
                    state.currentDevice = this.serialService.getPortInfo();
                    state.connectionError = null;
                } else {
                    state.currentDevice = null;
                }
                this.stateSubject.next(state);
            })
        );

        // Subscribe to errors
        this.subscriptions.add(
            this.serialService.error$.subscribe((error) => {
                const state = this.stateSubject.value;
                state.connectionError = error.message;
                state.isConnecting = false;
                this.stateSubject.next(state);
            })
        );

        // Load initial upload status and start auto-refresh
        void this.updateUploadStatus();
        this.startStatusRefresh();
    }

    private startStatusRefresh(): void {
        const refreshInterval = setInterval(() => {
            void this.updateUploadStatus();
        }, 5000);

        this.subscriptions.add({
            unsubscribe: () => clearInterval(refreshInterval)
        });
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public async connect(): Promise<void> {
        const state = this.stateSubject.value;
        if (!state.isSupported) {
            state.connectionError = 'Web Serial API is not supported in this browser';
            this.stateSubject.next(state);
            return;
        }

        state.isConnecting = true;
        state.connectionError = null;
        this.stateSubject.next(state);

        try {
            const config: Partial<SerialConnectionConfig> = {
                baudRate: 9600,
                dataBits: 8,
                stopBits: 1,
                parity: 'none',
                flowControl: false
            };

            const connected = await this.serialService.connect(undefined, config);

            if (!connected) {
                const state = this.stateSubject.value;
                state.connectionError = 'Failed to connect to serial device';
                state.isConnecting = false;
                this.stateSubject.next(state);
            }
        } catch (error) {
            const state = this.stateSubject.value;
            state.connectionError = error instanceof Error ? error.message : 'Unknown error occurred';
            state.isConnecting = false;
            this.stateSubject.next(state);
        }
    }

    public async disconnect(): Promise<void> {
        try {
            await this.serialService.disconnect();
            const state = this.stateSubject.value;
            //state.receivedData = [];
            this.stateSubject.next(state);
        } catch (error) {
            const state = this.stateSubject.value;
            state.connectionError = error instanceof Error ? error.message : 'Failed to disconnect';
            this.stateSubject.next(state);
        }
    }

    public async transfer(locationId?: string | null, afterDownload?: () => Promise<void>): Promise<void> {
        const state = this.stateSubject.value;
        if (!state.isConnected) return;

        this.autoDisconnectRequested = true;

        // Set transfer state
        state.isTransferring = true;
        state.transferProgress = 0;
        state.transferSpeedBps = 0;
        state.filesReceived = 0;
        this._transferSpeedStartTime = null;
        this._transferSpeedBytesReceived = 0;
        this.stateSubject.next(state);

        const preBuffer = await this.flushDeviceLogs();

        let block0;
        const initialSpill: Uint8Array | undefined = preBuffer;
        let file = 1;
        let yModemStarted = false;
        let transferTimestamp: number | null = null;

        const packetData = new Uint8Array(g_sz_packet_size[g_sz_codes.STX] + g_sz_packet_size.overhead);

        // If flushDeviceLogs already captured the SOH byte and any following
        // bytes, pre-fill the packet buffer so szGetPacket picks up from there.
        if (initialSpill) {
            packetData.set(initialSpill, 0);
        }
        let nextBlock0Offset = initialSpill?.length ?? 0;

        try {
            while ((block0 = await this.serialService.szGetPacket(packetData, nextBlock0Offset))) {
                nextBlock0Offset = 0; // reset for every subsequent outer-loop iteration
                this.szLog('info', `Requesting file ${file}...`);
                let fileData;
                let fileDataPtr = 0;
                let { size, spill, endReason } = block0;
                //let lastPacketSize;
                let firstEOT = true;

                yModemStarted = true;

                try {
                    if (size !== g_sz_packet_size[g_sz_codes.SOH] + g_sz_packet_size.overhead) {
                        throw new Error(`Unexpected Ymodem block 0 size: ${size}`);
                    }

                    const [filename, rest] = new TextDecoder().decode(packetData.slice(3, size - 2)).split('\0');
                    if (!filename) {
                        this.szLog('info', 'Received empty block 0 so ending import.');
                        break;
                    }

                    const fileSizeStr = rest.split(' ')[0];
                    const fileSize = parseInt(fileSizeStr);
                    if (isNaN(fileSize)) {
                        throw new Error(`Received invalid file size in block 0: ${fileSizeStr}`);
                    }
                    this.szLog(
                        'info',
                        `SzPerformNextGenImport: received file name ${filename} and file size ${fileSize}`
                    );

                    fileData = new Uint8Array(fileSize);

                    if (spill) {
                        packetData.set(spill, 0);
                    }

                    const currentState = this.stateSubject.value;
                    currentState.currentFileName = filename;
                    this.stateSubject.next(currentState);

                    await this.serialService.sendData(new Uint8Array([g_sz_codes.C]));
                    this._transferSpeedStartTime = Date.now();
                    this._transferSpeedBytesReceived = 0;
                    this._lastProgressUpdateMs = 0;

                    let transferComplete = false;
                    while (!transferComplete) {
                        if (spill) packetData.set(spill, 0);
                        ({ size, spill, endReason } = await this.serialService.szGetPacket(packetData, spill?.length));

                        if (endReason) {
                            if (endReason === 'EOT') {
                                if (firstEOT) {
                                    await this.serialService.sendData(new Uint8Array([g_sz_codes.NAK]));
                                    firstEOT = false;
                                    this.szLog('debug', 'SzPerformNextGenImport: First EOT received.');
                                    continue;
                                } else {
                                    await this.serialService.sendData(new Uint8Array([g_sz_codes.ACK]));
                                    this.szLog(
                                        'info',
                                        'SzPerformNextGenImport: Breaking loop because second EOT was received.'
                                    );
                                }
                            } else {
                                this.szLog(
                                    'info',
                                    'SzPerformNextGenImport: Breaking loop because stream ended with "done" flag being set.'
                                );
                            }
                            transferComplete = true;
                            break;
                        }

                        this.szValidatePacket(packetData, size);
                        let packet = packetData.slice(3, (size ?? 0) - 2);
                        const fileBytesLeft = fileSize - fileDataPtr;
                        if (packet.length > fileBytesLeft) {
                            packet = packet.slice(0, fileBytesLeft);
                        }
                        //lastPacketSize = packet.length;
                        fileData.set(packet, fileDataPtr);
                        fileDataPtr += packet.length;

                        this._transferSpeedBytesReceived += packet.length;
                        const now = Date.now();
                        const elapsedSec = (now - this._transferSpeedStartTime) / 1000;
                        const speedBps = elapsedSec > 0 ? Math.round(this._transferSpeedBytesReceived / elapsedSec) : 0;

                        // Update transfer progress — throttled to ~10 fps to avoid hammering Angular CD.
                        const fileProgress = (fileDataPtr / fileSize) * 100;
                        if (now - this._lastProgressUpdateMs >= 100) {
                            this._lastProgressUpdateMs = now;
                            const currentState = this.stateSubject.value;
                            currentState.transferProgress = fileProgress;
                            currentState.transferSpeedBps = speedBps;
                            this.stateSubject.next(currentState);
                        }
                    }

                    this.szLog('info', `SzPerformNextGenImport: Done importing file ${filename} from device.`);

                    if (fileDataPtr < fileSize) {
                        this.szLog(
                            'error',
                            `Data received for file ${filename} was less than expected. Expected ${fileSize} bytes but received ${fileDataPtr} bytes. File will not be uploaded.`
                        );
                        // Reset transfer progress for next file
                        const currentState = this.stateSubject.value;
                        currentState.transferProgress = 0;
                        this.stateSubject.next(currentState);
                    } else {
                        const dataString = new TextDecoder().decode(fileData);
                        this.szLog(
                            'info',
                            `SzPerformNextGenImport: Client side of import of file ${file} (${filename}) succeeded.`
                        );

                        // Update files received count
                        const currentState = this.stateSubject.value;
                        currentState.filesReceived = currentState.filesReceived++; //this.collectedFiles.length;
                        currentState.transferProgress = 0; // Reset for next file
                        this.stateSubject.next(currentState);

                        this.szLog(
                            'info',
                            `File ${filename} received. Queuing for upload immediately (total received: ${currentState.filesReceived}).`
                        );

                        // Initialize a shared timestamp for this transfer session on the first file
                        if (transferTimestamp === null) {
                            transferTimestamp = Date.now();
                            this.lastTransferTimestamp = transferTimestamp;
                        }

                        const metadata: FileMetadata = {
                            filename: filename,
                            device: 'NEXTGEN',
                            transferTimestamp: transferTimestamp,
                            fileSize: fileSize,
                            fileNumber: currentState.filesReceived,
                            locationId: locationId
                        };

                        void this.uploadService
                            .queueFileForUpload(dataString, metadata)
                            .then((): void => void this.updateUploadStatus())
                            .catch((err: unknown) =>
                                this.szLog('error', `Failed to queue ${filename} for upload: ${(err as Error).message}`)
                            );
                    }
                    await this.serialService.sendData(new Uint8Array([g_sz_codes.C]));

                    // Track transfer speed
                    this._transferSpeedStartTime = Date.now();
                    this._transferSpeedBytesReceived = 0;
                    file++;
                } catch (e: unknown) {
                    if ((e as YmodemError).fatal) {
                        throw e;
                    }
                    this.szLog(
                        'error',
                        `SzPerformNextGenImport: error occurred during Ymodem session: ${(e as Error).message}`
                    );
                    await this.serialService.sendData(new Uint8Array([g_sz_codes.C]));
                    file++;
                }
            }
            if (afterDownload && this.stateSubject.value.isConnected) {
                await afterDownload();
            }
        } catch (error: unknown) {
            const errorObj = error as YmodemError;
            if (!yModemStarted && errorObj.message === IMPORT_ERRORS.invalidHeaderByte) {
                errorObj.message +=
                    ' Maybe transfer was not started on the TetraGraph before it was started in TetraConnect';
            }
            this.szLog(
                'error',
                `SzPerformNextGenImport: a fatal error occurred during Ymodem session (aborting): ${errorObj.message}${
                    errorObj.details ? ` Details: ${errorObj.details}` : ''
                }`
            );
        } finally {
            // Always reset transfer state when done
            const finalState = this.stateSubject.value;
            finalState.isTransferring = false;
            finalState.transferProgress = 0;
            finalState.transferSpeedBps = 0;
            this._transferSpeedStartTime = null;
            this._transferSpeedBytesReceived = 0;
            this.stateSubject.next(finalState);
        }

        // If no files were received, ensure lastTransferTimestamp is cleared so auto-disconnect fires immediately
        if (transferTimestamp === null) {
            this.lastTransferTimestamp = null;
        }
        await this.updateUploadStatus();
        await this.evaluateAutoDisconnect();
    }

    public async updateUploadStatus(): Promise<void> {
        try {
            const uploadStatus = await this.uploadService.getUploadStatus();
            const fileList = await this.uploadService.getFileList();
            const state = this.stateSubject.value;
            state.uploadStatus = uploadStatus;
            state.fileList = fileList;
            this.stateSubject.next(state);
            await this.evaluateAutoDisconnect();
        } catch (error) {
            console.error('Failed to get upload status:', error);
        }
    }

    private async evaluateAutoDisconnect(): Promise<void> {
        if (!this.autoDisconnectRequested) return;

        const state = this.stateSubject.value;
        if (!state.isConnected || state.isTransferring) return;

        if (this.lastTransferTimestamp === null) {
            this.autoDisconnectRequested = false;
            await this.disconnect();
            return;
        }

        const relevantFiles = state.fileList.filter(
            (file) => file.metadata.transferTimestamp === this.lastTransferTimestamp
        );

        if (relevantFiles.length === 0) {
            return;
        }

        const hasPendingOrUploading = relevantFiles.some(
            (file) => file.status === 'pending' || file.status === 'uploading'
        );

        if (!hasPendingOrUploading) {
            this.autoDisconnectRequested = false;
            await this.disconnect();
        }
    }

    public async retryFailedUploads(): Promise<void> {
        try {
            this.szLog('info', 'Retrying failed uploads...');
            await this.uploadService.retryFailedUploads();
            await this.updateUploadStatus();
            this.szLog('info', 'Retry process completed');
        } catch (error: unknown) {
            const err = error as Error;
            this.szLog('error', `Failed to retry uploads: ${err.message}`);
        }
    }

    /**
     * Trigger upload for all failed and pending files (e.g., after returning from offline mode).
     * Ensures uploads will proceed regardless of checkbox state.
     */
    public async retryUpload(): Promise<void> {
        try {
            this.szLog('info', 'Starting upload for queued files...');
            await this.uploadService.retryUpload();
            await this.updateUploadStatus();
            this.szLog('info', 'Upload process completed');
        } catch (error: unknown) {
            const err = error as Error;
            this.szLog('error', `Failed to upload queued files: ${err.message}`);
        }
    }

    public async clearStoredFiles(): Promise<void> {
        try {
            await this.uploadService.clearAllFiles();
            await this.updateUploadStatus();
            this.szLog('info', 'All stored files cleared');
        } catch (error: unknown) {
            const err = error as Error;
            this.szLog('error', `Failed to clear stored files: ${err.message}`);
        }
    }

    public clearReceivedData(): void {
        const state = this.stateSubject.value;
        state.receivedData = [];
        this.stateSubject.next(state);
    }

    public async cleanupUploadedFiles(): Promise<void> {
        await this.uploadService.cleanupUploadedFiles();
    }

    /**
     * Import a file manually from the file system
     */
    public async importFileManually(filename: string, content: string, locationId: string): Promise<void> {
        try {
            this.szLog('info', `Importing file manually: ${filename}`);

            const metadata: FileMetadata = {
                filename: filename,
                device: 'MANUAL',
                transferTimestamp: Date.now(),
                fileSize: content.length,
                fileNumber: 0,
                locationId: locationId
            };

            await this.uploadService.queueFileForUpload(content, metadata);
            this.szLog('info', `File ${filename} queued for upload`);

            await this.updateUploadStatus();
        } catch (error: unknown) {
            this.szLog('error', `Failed to import file ${filename}: ${(error as Error).message}`);
            throw error;
        }
    }

    private szValidatePacket(packet: Uint8Array, size = packet.length) {
        const crcWord = packet[size - 2] * 256 + packet[size - 1];
        const crc = this.szCrc16(packet.slice(3, size - 2));

        if (crcWord != crc) {
            this.szLog('error', 'SzValidatePacket: Could not verify checksum. Throwing error.');

            const gSzMsgCsFailed =
                'Checksum failed. Perhaps a loose contact? Please check the connected USB cable that it is inserted correctly on the rear of the TetraGraph monitor and on the computer.';

            const err = new Error(gSzMsgCsFailed);
            throw err;
        }
        if (!this.szVerifyPacketNumbers(packet.slice(1, 3))) {
            this.szLog('error', 'SzValidatePacket: Could not verify packet numbers. Throwing error.');
            const gSzMsgUnexpPacket =
                'TetraGraph sent an unexpected data packet. Perhaps a loose contact? Please check the connected USB cable that it is inserted correctly on the rear of the TetraGraph monitor and on the computer.';

            const err = new Error(gSzMsgUnexpPacket);
            throw err;
        }
    }

    private szVerifyPacketNumbers([byte1, byte2]: Uint8Array): boolean {
        return byte1 + byte2 === 255;
    }

    private szCrc16(packet: Uint8Array): number {
        let uwCRC = 0;
        const poly = 0x1021;

        for (const byte of packet) {
            uwCRC = uwCRC ^ (byte << 8);

            for (let i = 0; i < 8; ++i) {
                uwCRC = uwCRC << 1;
                if (uwCRC & 0x10000) {
                    uwCRC = (uwCRC ^ poly) & 0xffff;
                }
            }
        }
        return uwCRC;
    }

    private szLog(loglevel: string, message: string): void {
        console.log(`[${loglevel}] ${message}`);
        const state = this.stateSubject.value;
        state.receivedData.push(message);
        this.stateSubject.next(state);
    }

    private async flushDeviceLogs(): Promise<Uint8Array | undefined> {
        this.szLog('info', 'Flushing device logs...');

        await this.serialService.sendData(new Uint8Array([0x13]));

        try {
            this.szLog('info', 'Waiting for modem transmit message...');

            // Send C to trigger the Ymodem transfer, then wait for the SOH
            // byte that marks the start of the first packet. Return that byte
            // and whatever followed it in the same chunk so the caller can
            // pass it as the initial spill to szGetPacket.
            await this.serialService.sendData(new Uint8Array([g_sz_codes.C]));

            this.szLog('info', 'Waiting for SOH (start of first Ymodem packet)...');
            const preBuffer = await this.serialService.szWaitForSOH(60000);
            this.szLog('info', `SOH received, ${preBuffer.length} byte(s) buffered`);
            this.szLog('info', 'Device logs flushed');
            return preBuffer;
        } catch {
            this.szLog('error', 'Timeout waiting for modem transmit message or SOH');
        }

        this.szLog('info', 'Device logs flushed');
        return undefined;
    }
}
